import React, { useRef } from 'react';
import { Tenant, Room, BoardingHouseInfo } from '../types';
import Icon from './ui/Icon';
import { formatDateForDisplay } from '../utils/dateUtils';

const numberToWordsVND = (num: number | undefined | null): string => {
    if (num === null || typeof num === 'undefined' || num === 0) return 'Không đồng';
    const units = ["", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
    const teens = ["mười", "mười một", "mười hai", "mười ba", "mười bốn", "mười lăm", "mười sáu", "mười bảy", "mười tám", "mười chín"];
    const tens = ["", "mười", "hai mươi", "ba mươi", "bốn mươi", "năm mươi", "sáu mươi", "bảy mươi", "tám mươi", "chín mươi"];
    const powers = ["", "nghìn", "triệu", "tỷ"];
    const numToWords = (n: number): string => {
        if (n < 10) return units[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) {
            const ten = Math.floor(n / 10);
            const unit = n % 10;
            if (unit === 0) return tens[ten];
            if (unit === 1) return tens[ten] + " mốt";
            if (unit === 5) return tens[ten] + " lăm";
            return tens[ten] + " " + units[unit];
        }
        if (n < 1000) {
            const hundred = Math.floor(n / 100);
            const rest = n % 100;
            let result = units[hundred] + " trăm";
            if (rest > 0) {
                if (rest < 10) result += " linh " + units[rest];
                else result += " " + numToWords(rest);
            }
            return result;
        }
        return "";
    };
    let result = "";
    let powerIndex = 0;
    let tempNum = num;
    while (tempNum > 0) {
        const chunk = tempNum % 1000;
        if (chunk > 0) {
            const chunkWords = numToWords(chunk);
            result = chunkWords + " " + powers[powerIndex] + " " + result;
        }
        tempNum = Math.floor(tempNum / 1000);
        powerIndex++;
    }
    let finalResult = result.trim();
    return finalResult.charAt(0).toUpperCase() + finalResult.slice(1) + " đồng";
};

const ContractModal: React.FC<{
    tenant: Tenant | null;
    room: Room | null;
    boardingHouseInfo: BoardingHouseInfo;
    onClose: () => void;
}> = ({ tenant, room, boardingHouseInfo, onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);

    // Debug: Log boardingHouseInfo data
    console.log('ContractModal boardingHouseInfo:', boardingHouseInfo);

    const loadScript = (src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                // already loading or loaded
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error('Failed to load script')));
                return;
            }
            const s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('Failed to load script'));
            document.head.appendChild(s);
        });
    };

    const handleExportWord = async () => {
        const content = printRef.current;
        if (!content || !tenant) return;

        try {
            // Small delay to ensure DOM is fully updated
            await new Promise(r => setTimeout(r, 100));
            
            if (typeof (window as any).htmlToDocx !== 'function') {
                // Load html-to-docx from CDN (browser-compatible version)
                try {
                    await loadScript('https://unpkg.com/html-to-docx@1.8.0/dist/html-to-docx.umd.js');
                    await new Promise(r => setTimeout(r, 100));
                } catch (e) {
                    throw new Error('Không thể tải thư viện html-to-docx. Vui lòng kiểm tra kết nối.');
                }
            }
            if (typeof (window as any).htmlToDocx !== 'function') {
                throw new Error('Thư viện html-to-docx không khả dụng sau khi tải.');
            }

            if (typeof (window as any).saveAs !== 'function') {
                // Load FileSaver.js for saving files
                await loadScript('/vendor/FileSaver.min.js');
                await new Promise(r => setTimeout(r, 50));
            }
            if (typeof (window as any).saveAs !== 'function') {
                throw new Error('Thư viện FileSaver không khả dụng sau khi tải.');
            }

            // Debug: Log the HTML content being exported
            console.log('Contract HTML content for Word export:', content.innerHTML);

            const fileBuffer = await (window as any).htmlToDocx(content.innerHTML, null, {
                orientation: 'portrait',
                margins: { top: 1440, right: 1800, bottom: 1440, left: 1800 }
            });
            (window as any).saveAs(fileBuffer, `HopDongThueTro_${tenant.name.replace(/\s/g, '_')}.docx`);
        } catch (err: any) {
            import('../utils/logger').then(l => l.error('Export to Word failed', err));
            alert(`Không thể xuất Word: ${err?.message ?? String(err)}. Vui lòng thử lại hoặc kiểm tra kết nối.`);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        if (printContent) {
            const WinPrint = window.open('', '', 'width=830,height=900');
            WinPrint?.document.write(`
                <html>
                    <head>
                        <title>In Hợp đồng</title>
                        <style>
                            body { font-family: 'Times New Roman', Times, serif; font-size: 13pt; line-height: 1.5; color: #000; margin: 0; padding: 0; }
                            .page { width: 210mm; min-height: 297mm; padding: 20mm 25mm; margin: 0 auto; box-sizing: border-box; }
                            .header, .title { text-align: center; font-weight: bold; }
                            .header { margin-bottom: 5px; }
                            .title { font-size: 14pt; margin: 20px 0; }
                            p { margin: 5px 0; text-align: justify; }
                            .party-info p { margin: 2px 0; }
                            .signatures { display: flex; justify-content: space-around; margin-top: 80px; }
                            .signature-block { text-align: center; }
                            .signature-block .role { font-weight: bold; }
                            .signature-block .instruction { font-style: italic; }
                        </style>
                    </head>
                    <body><div class="page">${printContent.innerHTML}</div></body>
                </html>
            `);
            WinPrint?.document.close();
            WinPrint?.focus();
            WinPrint?.print();
            WinPrint?.close();
        }
    };

    if (!tenant || !room) return null;
    
    const today = new Date();
    const fullAddress = [boardingHouseInfo.address, boardingHouseInfo.ward, boardingHouseInfo.city].filter(Boolean).join(', ');
    const depositAmount = (tenant.depositAmount && tenant.depositAmount > 0) ? tenant.depositAmount : room.price;
    const defaultIssuePlace = "Cục Cảnh sát quản lý hành chính về trật tự xã hội";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[95vh] flex flex-col">
                <div className="flex-shrink-0 p-4 border-b flex justify-between items-center no-print">
                    <h2 className="text-xl font-semibold">Hợp đồng thuê phòng trọ</h2>
                    <div className="flex items-center gap-4">
                       <button onClick={handleExportWord} className="btn-secondary flex items-center gap-2"><Icon name="word" className="w-5 h-5"/> Tải Word</button>
                       <button onClick={handlePrint} className="btn-secondary flex items-center gap-2"><Icon name="print" className="w-5 h-5"/> In</button>
                       <button onClick={onClose} className="text-gray-500 hover:text-gray-800">&times;</button>
                    </div>
                </div>
                <div className="flex-grow overflow-y-auto p-6">
                    <div ref={printRef} className="contract-container printable-document">
                        <p className="header text-center font-bold">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>
                        <p className="header text-center font-bold" style={{textDecoration: 'underline'}}>Độc lập – Tự do – Hạnh phúc</p>
                        <p className="title text-center font-bold text-lg my-4">HỢP ĐỒNG THUÊ PHÒNG TRỌ</p>
                        
                        <p>Hôm nay, ngày {today.getDate()} tháng {today.getMonth() + 1} năm {today.getFullYear()}, tại {boardingHouseInfo.name || '...'}, địa chỉ: {fullAddress || '...'}</p>
                        <p>Chúng tôi ký tên dưới đây gồm có:</p>
                        <br/>
                        <div className="party-info">
                            <p className="font-bold">BÊN CHO THUÊ PHÒNG TRÓ (gọi tắt là Bên A):</p>
                            <p>Ông/bà: <span className="font-bold">{boardingHouseInfo.landlordName || '........................'}</span>. Sinh ngày: {formatDateForDisplay(boardingHouseInfo.landlordDob) || '........................'}</p>
                            <p>CMND/CCCD số: {boardingHouseInfo.landlordIdNumber || '........................'}</p>
                            <p>Thường trú tại: {boardingHouseInfo.address || '..................................................................'}</p>
                        </div>
                        <br/>
                        <div className="party-info">
                            <p className="font-bold">BÊN THUÊ PHÒNG TRỌ (gọi tắt là Bên B):</p>
                            <p>Ông/bà: <span className="font-bold">{tenant.name || '........................'}</span>. Sinh ngày: {formatDateForDisplay(tenant.dateOfBirth) || '........................'}</p>
                            <p>Số điện thoại: {tenant.phone || '........................'}</p>
                            <p>CMND/CCCD số: {tenant.idNumber || '........................'} cấp ngày: {formatDateForDisplay(tenant.idIssueDate) || '........................'} nơi cấp: {tenant.idIssuePlace || defaultIssuePlace}</p>
                            <p>Thường trú tại: {tenant.permanentAddress || '..................................................................'}</p>
                        </div>
                        <br/>
                        <p>Sau khi thỏa thuận, hai bên thống nhất như sau:</p>
                        <p className="font-bold">1. Nội dung thuê phòng trọ</p>
                        <p>Bên A cho Bên B thuê 01 phòng trọ số <span className="font-bold">{room.roomNumber}</span> tại {boardingHouseInfo.name}. Địa chỉ: {fullAddress}. Với thời hạn là: 2 năm, giá thuê: {new Intl.NumberFormat('vi-VN').format(room.price)} vnđ/ tháng (Bằng chữ: {numberToWordsVND(room.price)}). Chưa bao gồm chi phí: điện sinh hoạt, nước, rác.</p>
                        <p className="font-bold">2. Trách nhiệm Bên A</p>
                        <p>Đảm bảo căn nhà cho thuê không có tranh chấp, khiếu kiện.</p>
                        <p>Đăng ký với chính quyền địa phương về thủ tục cho thuê phòng trọ.</p>
                        <p className="font-bold">3. Trách nhiệm Bên B</p>
                        <p>Đặt cọc với số tiền là {new Intl.NumberFormat('vi-VN').format(depositAmount || 0)} đồng (Bằng chữ: {numberToWordsVND(depositAmount)}), thanh toán tiền thuê phòng hàng tháng vào ngày 15 hàng tháng + tiền điện + nước + các khoản phí phụ khác.</p>
                        <p>Đảm bảo các thiết bị và sửa chữa các hư hỏng trong phòng trong khi sử dụng. Nếu không sửa chữa thì khi trả phòng, bên A sẽ trừ vào tiền đặt cọc, giá trị cụ thể được tính theo giá thị trường.</p>
                        <p>Chỉ sử dụng phòng trọ vào mục đích ở, với số lượng tối đa không quá 04 người (kể cả trẻ em); không chứa các thiết bị gây cháy nổ, hàng cấm... cung cấp giấy tờ tùy thân để đăng ký tạm trú theo quy định, giữ gìn an ninh trật tự, nếp sống văn hóa đô thị; không tụ tập nhậu nhẹt, cờ bạc và các hành vi vi phạm pháp luật khác.</p>
                        <p>Không được tự ý cải tạo kiếm trúc phòng hoặc trang trí ảnh hưởng tới tường, cột, nền... Nếu có nhu cầu trên phải trao đổi với bên A để được thống nhất</p>
                        <p className="font-bold">4. Điều khoản thực hiện</p>
                        <p>Hai bên phải tạo điều kiện cho nhau thực hiện hợp đồng, nếu bên nào vi phạm các điều khoản trên thì bên còn lại có quyền đơn phương chấm dứt hợp đồng và bên sai phạm hợp đồng phải bồi thường thiệt hại.</p>
                        <p>Hai bên nghiêm túc thực hiện những quy định trên trong thời hạn cho thuê, nếu bên A lấy phòng phải báo cho bên B ít nhất 01 tháng, hoặc ngược lại.</p>
                        <p>Hợp đồng được in thành hai bản, có giá trị như nhau và mỗi bên giữ một bản để cùng nhau thực hiện.</p>
                        <br/><br/>
                        <div className="signatures flex justify-around mt-12">
                            <div className="signature-block text-center"><p className="role">Bên B</p><p className="instruction">(Ký, ghi rõ họ tên)</p></div>
                            <div className="signature-block text-center"><p className="role">Bên A</p><p className="instruction">(Ký, ghi rõ họ tên)</p></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractModal;